// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------
import * as React from 'react';
import clsx from 'clsx';
import ErrorMessage from './../../components/ErrorMessage/ErrorMessage';
import { Icon } from 'office-ui-fabric-react';
import { Dropdown, IDropdownOption, IDropdownStyles } from 'office-ui-fabric-react/lib/Dropdown';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logPageAction } from 'src/lib/ApplicationInsights';
import { actionStatusReset, LoadingState } from './../../store/actionStatus';
import { GET_SERVICE_REQUEST_DATA } from './../../store/deviceInfo/claimOrder';
import { GET_DEVICE_INFO_DATA } from './../../store/deviceInfo/deviceInfo';
import DeviceInputs from './DeviceInputs';
import { statusSelector } from './selector';
import Styles from './serviceRequestAndDeviceLookup.module.scss';
import ServiceRequestInput from './ServiceRequestInput';
import DeviceWarrantyInput from './DeviceWarrantyInput';
import { getTierName } from './../../lib/userTierConfig';

const TIER_30 = '30';
const TIER_13 = '13';
const TIER_14 = '14';
interface OptionsType {
    key: string;
    text?: string;
}

const dropdownStyles: Partial<IDropdownStyles> = {
    dropdown: { width: 197 },
};

const optionsKeys: () => OptionsType[] = () => {
    if (getTierName() === TIER_30) {
        return [{ key: 'check_your_warranty' }];
    }
    if (getTierName() === TIER_13 || getTierName() === TIER_14) {
        return [
            { key: 'create_a_repair_order' },
            { key: 'manage_an_existing_order' },
            { key: 'check_your_warranty' },
            { key: 'create_a_commercial_exchange_order' },
        ];
    }
    return [
        { key: 'create_a_repair_order' },
        { key: 'manage_an_existing_order' },
        { key: 'check_your_warranty' },
    ];
};

export default function ServiceRequestAndDeviceLookup() {
    const { t } = useTranslation();
    React.useEffect(() => {
        setTimeout(() => {
            const msDropDown = document.getElementsByClassName('ms-Dropdown')[0];
            msDropDown ? msDropDown.removeAttribute('tabIndex') : null;
            msDropDown && msDropDown.firstElementChild ? msDropDown.firstElementChild.setAttribute('tabIndex', '0') : null;
        }, 0);
    }, []);

    const onDropdownFocus = () => {
        const title = document.getElementsByClassName('ms-Dropdown-title')[0];
        title ? title.setAttribute('style', 'border-color:black;outline-style:auto;outline-offset:3px') : '';
    };
    const onDropdownBlur = () => {
        const title = document.getElementsByClassName('ms-Dropdown-title')[0];
        title ? title.setAttribute('style', 'outline-style:none') : '';
    };

    const dispatch = useDispatch();
    const [searchBy, setSearchBy] = React.useState('');
    const [validationError, setValidationError] = React.useState('');

    const options: IDropdownOption[] = optionsKeys().map((option: OptionsType): IDropdownOption => {
        const text: string = option.text || option.key;
        return {
            key: option.key,
            text: t(text),
        };
    });

    function orderTypeOnChange(event: React.FormEvent<HTMLDivElement>, selectedOption?: IDropdownOption
    ) {
        if (selectedOption) {
            logPageAction('Serial number or order number dropdown item selected', { itemSelected: selectedOption.text });

            const { key } = selectedOption;
            setSearchBy(key.toString());
            setValidationError('');
            dispatch(actionStatusReset({ actionName: GET_DEVICE_INFO_DATA }));
            dispatch(actionStatusReset({ actionName: GET_SERVICE_REQUEST_DATA }));
            dispatch({ type: 'SET_SELECTED_OPTION_DATA', payload: key.toString() });
        }
    }

    function onError(errorMessage: string) {
        setValidationError(errorMessage);
    }

    function renderInputs() {
        switch (searchBy) {
            case 'create_a_repair_order':
                return <DeviceInputs onError={onError} />;
            case 'manage_an_existing_order':
                return <ServiceRequestInput onError={onError} />;
            case 'check_your_warranty':
                return <DeviceWarrantyInput onError={onError} />;
            case 'create_a_commercial_exchange_order':
                return <DeviceInputs onError={onError} />;
            default:
                return null;
        }
    }

    function descriptions() {
        const descriptionValues = {
            create_a_repair_order: {
                header: 'create_repair_order_header',
                description: 'create_repair_order_description',
            },
            manage_an_existing_order: {
                header: 'manage_existing_order_header',
                description: 'manage_existing_order_description',
            },
            check_your_warranty: {
                header: 'check_warranty_status_header',
                description: 'check_warranty_status_description',
            },
        };

        const { header, description } = descriptionValues[searchBy] || { header: '', description: '' };

        return (<>
            <div className={clsx(Styles.inputsSection, Styles.descriptionContainer)}>
                <h2 className={Styles.descriptionHeader}> {t(header)}</h2>
            </div>
            <div className={Styles.inputsSection}>
                <span className={Styles.description}>{t(description)}</span>
            </div>
        </>);
    }

    return (
        <div className={Styles.container}>
            <RenderError validationError={validationError} />
            <div className={Styles.inputsSection}>
                <Dropdown
                    id="selectOptionId"
                    label={t('please_select_option')}
                    options={options}
                    styles={dropdownStyles}
                    onFocus={onDropdownFocus}
                    onBlur={onDropdownBlur}
                    onChange={orderTypeOnChange}
                    placeholder="Please select an option"
                />
                {renderInputs()}
            </div>
            {descriptions()}
        </div>
    );
}

export function RenderError({ validationError }: { validationError: string }) {
    const { t } = useTranslation();

    const orderNumberLoadingState = useSelector(state => statusSelector(state)(GET_SERVICE_REQUEST_DATA));
    const serialNumberLoadingState = useSelector(state => statusSelector(state)(GET_DEVICE_INFO_DATA));

    let errorText = '';
    if (orderNumberLoadingState.status === LoadingState.rejected
        || serialNumberLoadingState.status === LoadingState.rejected || validationError) {
        errorText = validationError || t('error_occurred');
    }

    if (orderNumberLoadingState.error && orderNumberLoadingState.error.status === 404) {
        errorText = t('order_number_not_found');
    } else if (serialNumberLoadingState.error && serialNumberLoadingState.error.status === 404) {
        errorText = t('serial_number_not_found');
    }

    const renderWarningIcon = () => {
        return (
            <Icon
                className={Styles.errorIcon}
                iconName="Warning"
            />
        );
    };
    const traceData = {
        errorText,
        orderNumberLoadingState,
        serialNumberLoadingState,
        validationError,
    };
    return (
        <ErrorMessage prependMessage={renderWarningIcon} message={errorText} traceData={traceData} />
    );
}